import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gql } from '../lib/api';
import {
  DELETE_LOAN_MUTATION,
  LOANS_QUERY,
  UPSERT_LOAN_MUTATION,
} from '../graphql/queries';
import { useUserGroups } from '../hooks/useUserGroups';

interface Loan {
  loanId: string;
  name: string;
  lender: string | null;
  startDate: string | null;
  initialAmount: number | null;
  outstanding: number;
  monthlyPayment: number;
  interestRate: number | null;
  endDate: string | null;
  notes: string | null;
  updatedAt: string | null;
}

interface LoanDraft {
  loanId: string | null;
  name: string;
  lender: string;
  startDate: string;
  initialAmount: string;
  outstanding: string;
  monthlyPayment: string;
  interestRate: string;
  endDate: string;
  notes: string;
}

const emptyDraft: LoanDraft = {
  loanId: null,
  name: '',
  lender: '',
  startDate: '',
  initialAmount: '',
  outstanding: '',
  monthlyPayment: '',
  interestRate: '',
  endDate: '',
  notes: '',
};

const toLoanInput = (d: LoanDraft) => ({
  name: d.name.trim(),
  lender: d.lender.trim() || null,
  startDate: d.startDate || null,
  initialAmount: d.initialAmount === '' ? null : Number(d.initialAmount),
  outstanding: Number(d.outstanding),
  monthlyPayment: Number(d.monthlyPayment),
  interestRate: d.interestRate === '' ? null : Number(d.interestRate),
  endDate: d.endDate || null,
  notes: d.notes.trim() || null,
});

export const LoanPage = () => {
  const groups = useUserGroups();
  const isAdmin = groups.includes('admin');
  const qc = useQueryClient();

  const loansQ = useQuery({
    queryKey: ['loans'],
    queryFn: () => gql<{ loans: Loan[] }>(LOANS_QUERY),
  });
  const loans = loansQ.data?.loans ?? [];

  const [draft, setDraft] = useState<LoanDraft>(emptyDraft);
  const [showForm, setShowForm] = useState(false);

  const upsert = useMutation({
    mutationFn: (vars: { loanId: string | null; input: ReturnType<typeof toLoanInput> }) =>
      gql(UPSERT_LOAN_MUTATION, { loanId: vars.loanId ?? undefined, input: vars.input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['loans'] });
      setDraft(emptyDraft);
      setShowForm(false);
    },
  });

  const remove = useMutation({
    mutationFn: (loanId: string) => gql(DELETE_LOAN_MUTATION, { loanId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['loans'] });
    },
  });

  const summary = useMemo(() => {
    const outstanding = loans.reduce((s, l) => s + (l.outstanding ?? 0), 0);
    const monthly = loans.reduce((s, l) => s + (l.monthlyPayment ?? 0), 0);
    const longestMonths =
      loans.length > 0 && monthly > 0
        ? Math.max(
            ...loans.map((l) =>
              l.monthlyPayment > 0 ? Math.ceil(l.outstanding / l.monthlyPayment) : 0,
            ),
          )
        : 0;
    return { outstanding, monthly, longestMonths };
  }, [loans]);

  if (!isAdmin) {
    return (
      <div style={cardSection}>
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>借入</h1>
        <p style={{ marginTop: '0.75rem', color: '#868e96' }}>
          このページは admin 権限のユーザーのみが閲覧・編集できます。
        </p>
      </div>
    );
  }

  const startEdit = (l: Loan) => {
    setDraft({
      loanId: l.loanId,
      name: l.name,
      lender: l.lender ?? '',
      startDate: l.startDate ?? '',
      initialAmount: l.initialAmount != null ? String(l.initialAmount) : '',
      outstanding: String(l.outstanding ?? 0),
      monthlyPayment: String(l.monthlyPayment ?? 0),
      interestRate: l.interestRate != null ? String(l.interestRate) : '',
      endDate: l.endDate ?? '',
      notes: l.notes ?? '',
    });
    setShowForm(true);
  };

  const startNew = () => {
    setDraft(emptyDraft);
    setShowForm(true);
  };

  const submit = () => {
    if (!draft.name.trim()) {
      alert('借入名は必須です。');
      return;
    }
    if (draft.outstanding === '' || !Number.isFinite(Number(draft.outstanding))) {
      alert('残高は数値で入力してください。');
      return;
    }
    if (draft.monthlyPayment === '' || !Number.isFinite(Number(draft.monthlyPayment))) {
      alert('月返済額は数値で入力してください。');
      return;
    }
    upsert.mutate({ loanId: draft.loanId, input: toLoanInput(draft) });
  };

  return (
    <div>
      <div style={pageHeader}>
        <h1 style={pageTitle}>借入</h1>
        <button type="button" onClick={startNew} style={primaryBtn}>
          ＋ 新規追加
        </button>
      </div>

      <section style={notice}>
        <p style={{ margin: 0 }}>
          全社の借入を管理します。トップページの「借入カード」はここで登録した値の合計が表示されます（店舗切替の影響を受けません）。
        </p>
      </section>

      <section style={kpiGrid}>
        <KpiCard label="借入残高合計" value={fmtYen(summary.outstanding)} />
        <KpiCard label="毎月返済額合計" value={fmtYen(summary.monthly)} />
        <KpiCard
          label="完済までの最長月"
          value={summary.longestMonths > 0 ? `${summary.longestMonths} ヶ月` : '—'}
        />
      </section>

      {showForm && (
        <section style={cardSection}>
          <h2 style={sectionTitle}>{draft.loanId ? '編集' : '新規追加'}</h2>
          <div style={{ display: 'grid', gap: '0.6rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <label>
              借入名
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="日本政策金融公庫 創業融資"
                style={input}
              />
            </label>
            <label>
              貸主（任意）
              <input
                type="text"
                value={draft.lender}
                onChange={(e) => setDraft({ ...draft, lender: e.target.value })}
                placeholder="日本政策金融公庫"
                style={input}
              />
            </label>
            <label>
              借入実行日（任意）
              <input
                type="date"
                value={draft.startDate}
                onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
                style={input}
              />
            </label>
            <label>
              当初借入額（任意）
              <input
                type="number"
                step={10000}
                min={0}
                value={draft.initialAmount}
                onChange={(e) => setDraft({ ...draft, initialAmount: e.target.value })}
                placeholder="10000000"
                style={input}
              />
            </label>
            <label>
              現在の残高
              <input
                type="number"
                step={10000}
                min={0}
                value={draft.outstanding}
                onChange={(e) => setDraft({ ...draft, outstanding: e.target.value })}
                placeholder="6500000"
                style={input}
              />
            </label>
            <label>
              月返済額
              <input
                type="number"
                step={1000}
                min={0}
                value={draft.monthlyPayment}
                onChange={(e) => setDraft({ ...draft, monthlyPayment: e.target.value })}
                placeholder="120000"
                style={input}
              />
            </label>
            <label>
              年利（%、任意）
              <input
                type="number"
                step={0.01}
                min={0}
                value={draft.interestRate}
                onChange={(e) => setDraft({ ...draft, interestRate: e.target.value })}
                placeholder="2.4"
                style={input}
              />
            </label>
            <label>
              完済予定日（任意）
              <input
                type="date"
                value={draft.endDate}
                onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
                style={input}
              />
            </label>
            <label style={{ gridColumn: '1 / 3' }}>
              備考（任意）
              <textarea
                rows={2}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                style={{ ...input, fontFamily: 'inherit' }}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
            <button type="button" onClick={submit} disabled={upsert.isPending} style={primaryBtn}>
              {upsert.isPending ? '保存中…' : draft.loanId ? '上書き保存' : '登録'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setDraft(emptyDraft);
              }}
              style={secondaryBtn}
            >
              キャンセル
            </button>
          </div>
          {upsert.isError && (
            <p style={{ color: '#aa0b56', marginTop: '0.5rem' }}>
              エラー：{(upsert.error as Error).message}
            </p>
          )}
        </section>
      )}

      <section style={cardSection}>
        <h2 style={sectionTitle}>登録済み借入（{loans.length} 件）</h2>
        {loansQ.isLoading ? (
          <p style={muted}>読込中…</p>
        ) : loans.length === 0 ? (
          <p style={muted}>まだ登録されていません。「＋ 新規追加」から登録してください。</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>借入名 / 貸主</th>
                <th style={{ ...th, textAlign: 'right' }}>残高</th>
                <th style={{ ...th, textAlign: 'right' }}>月返済額</th>
                <th style={{ ...th, textAlign: 'right' }}>年利</th>
                <th style={{ ...th, textAlign: 'right' }}>完済まで</th>
                <th style={th}>完済予定</th>
                <th style={th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l) => {
                const monthsLeft =
                  l.monthlyPayment > 0 ? Math.ceil(l.outstanding / l.monthlyPayment) : 0;
                return (
                  <tr key={l.loanId}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{l.name}</div>
                      <div style={{ color: '#868e96', fontSize: '0.78rem' }}>
                        {l.lender ?? '—'} / 開始 {l.startDate ?? '—'}
                      </div>
                      {l.notes && (
                        <div style={{ color: '#495057', fontSize: '0.78rem', marginTop: 2 }}>
                          {l.notes}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtYen(l.outstanding)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtYen(l.monthlyPayment)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {l.interestRate != null ? `${l.interestRate.toFixed(2)}%` : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {monthsLeft > 0 ? `${monthsLeft} ヶ月` : '—'}
                    </td>
                    <td style={td}>{l.endDate ?? '—'}</td>
                    <td style={td}>
                      <button type="button" onClick={() => startEdit(l)} style={linkBtn}>
                        編集
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`「${l.name}」を削除します。よろしいですか？`)) {
                            remove.mutate(l.loanId);
                          }
                        }}
                        style={{ ...linkBtn, color: '#aa0b56', marginLeft: 4 }}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

interface KpiCardProps {
  label: string;
  value: string;
}
const KpiCard = ({ label, value }: KpiCardProps) => (
  <div style={kpiCardStyle}>
    <div style={kpiLabel}>{label}</div>
    <div style={kpiValue}>{value}</div>
  </div>
);

const fmtYen = (n: number | null | undefined): string =>
  typeof n === 'number' ? `¥${Math.round(n).toLocaleString()}` : '—';

const pageHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};
const pageTitle: React.CSSProperties = { margin: 0, fontSize: '1.4rem' };
const cardSection: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const notice: React.CSSProperties = {
  background: '#e7f5ff',
  border: '1px solid #a5d8ff',
  color: '#1864ab',
  borderRadius: 8,
  padding: '0.6rem 0.9rem',
  marginBottom: '1rem',
  fontSize: '0.85rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.9rem' };
const input: React.CSSProperties = {
  display: 'block',
  marginTop: 4,
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  width: '100%',
};
const primaryBtn: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: 6,
  border: '1px solid #aa0b56',
  background: '#aa0b56',
  color: '#fff',
  cursor: 'pointer',
};
const secondaryBtn: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
  color: '#495057',
  cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  borderRadius: 4,
  border: '1px solid #dee2e6',
  background: '#fff',
  color: '#1864ab',
  cursor: 'pointer',
  fontSize: '0.8rem',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #f1f3f5',
  verticalAlign: 'top',
};
const kpiGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1rem',
};
const kpiCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '0.9rem 1rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
};
const kpiLabel: React.CSSProperties = { color: '#6c757d', fontSize: '0.8rem' };
const kpiValue: React.CSSProperties = { fontSize: '1.4rem', fontWeight: 700, marginTop: 4 };
